// Dönem çipleri — bölüm TAVANI / soru TERCİHİ (Task 10, tasarım sözleşmesi 1B/5A/6A).
// fieldset+legend (görünür etiket) + dört aria-pressed butonu. Tab ile gezilir,
// Space/Enter toggle eder (native button davranışı). Bölüm tavanı dışında kalan
// seçim ÜSTÜ ÇİZİLİ DEĞİL: kesik çerçeve + soluk metin, tıklanabilir kalır ve
// aria-describedby ile sonuç satırını işaret eder.
import { useEffect, useRef, useState } from "react";
import { ALL_PERIODS, PERIOD_LABELS, type PeriodType } from "@mahir/shared";
import { PERIOD_COPY, PERIOD_SHORT_LABELS, togglePeriod } from "../../lib/period-ui";

interface PeriodChipsProps {
  legend: string;
  help?: string;
  value: readonly PeriodType[];
  // Soru çiplerinde bölümün tavanı; bölüm çiplerinde verilmez.
  ceiling?: readonly PeriodType[];
  disabled?: boolean;
  // Kilitli sürümde çiplerin ALTINDA görünen satır (2A).
  lockedNote?: string | null;
  // Tavan dışı çiplerin işaret ettiği sonuç satırının id'si (6A).
  describedById?: string;
  onChange: (next: PeriodType[]) => void;
}

// Seçili/seçilmemiş/tavan-dışı çip stilleri — hepsi token'dan (5A, yeni hex yok).
function chipStyle(selected: boolean, outside: boolean, disabled: boolean): React.CSSProperties {
  const base: React.CSSProperties = {
    borderRadius: 999,
    padding: "4px 12px",
    fontSize: "0.75rem",
    fontWeight: 600,
    lineHeight: 1.4,
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.6 : 1,
    display: "inline-flex",
    alignItems: "center",
    color: "var(--color-text-2)",
    background: "color-mix(in srgb, var(--color-primary) 6%, white)",
    border: "1px solid color-mix(in srgb, var(--color-primary) 25%, transparent)",
  };
  if (selected) {
    base.border = "2px solid var(--color-primary)";
    base.background = "color-mix(in srgb, var(--color-primary) 10%, white)";
    // 2px çerçeve 1px'e göre yüksekliği şişirmesin.
    base.padding = "3px 11px";
  }
  if (outside) {
    base.color = "var(--color-text-3)";
    base.border = "1px dashed color-mix(in srgb, var(--color-text-3) 40%, transparent)";
    base.background = "transparent";
    base.padding = "4px 12px";
  }
  return base;
}

export function PeriodChips({
  legend,
  help,
  value,
  ceiling,
  disabled = false,
  lockedNote = null,
  describedById,
  onChange,
}: PeriodChipsProps): JSX.Element {
  // "En az bir dönem seçili kalmalı" — 1,5 sn görünür uyarı (2A sınır hücresi).
  const [lastChipWarning, setLastChipWarning] = useState(false);
  const warnTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (warnTimer.current) clearTimeout(warnTimer.current);
    },
    [],
  );

  function onChipClick(period: PeriodType, isLastSelected: boolean) {
    if (isLastSelected) {
      setLastChipWarning(true);
      if (warnTimer.current) clearTimeout(warnTimer.current);
      warnTimer.current = setTimeout(() => setLastChipWarning(false), 1500);
      return;
    }
    onChange(togglePeriod(value, period));
  }

  return (
    <fieldset style={{ border: "none", padding: 0, margin: 0, minWidth: 0 }}>
      <legend
        style={{
          padding: 0,
          fontSize: "0.75rem",
          color: "var(--color-text-3)",
          marginBottom: 6,
        }}
      >
        {legend}
      </legend>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
        {ALL_PERIODS.map((p) => {
          const selected = value.includes(p);
          const outside = Boolean(ceiling) && selected && !ceiling?.includes(p);
          const isLastSelected = selected && value.length === 1;
          return (
            <button
              key={p}
              type="button"
              className="period-chip"
              aria-pressed={selected}
              aria-disabled={isLastSelected || undefined}
              aria-describedby={outside ? describedById : undefined}
              disabled={disabled}
              title={PERIOD_LABELS[p]}
              onClick={() => onChipClick(p, isLastSelected)}
              style={chipStyle(selected, outside, disabled)}
            >
              {selected ? "✓ " : ""}
              {PERIOD_SHORT_LABELS[p]}
            </button>
          );
        })}
      </div>
      {help ? (
        <p style={{ fontSize: "0.72rem", color: "var(--color-text-3)", marginTop: 6 }}>{help}</p>
      ) : null}
      {lastChipWarning ? (
        <p
          role="status"
          style={{
            marginTop: 6,
            fontSize: "0.75rem",
            color: "var(--color-warning-text)",
            background: "var(--color-warning-bg)",
            border: "1px solid var(--color-warning-border)",
            borderRadius: 6,
            padding: "4px 8px",
            display: "inline-block",
          }}
        >
          {PERIOD_COPY.lastChipWarning}
        </p>
      ) : null}
      {lockedNote ? (
        <p style={{ marginTop: 6, fontSize: "0.75rem", color: "var(--color-text-3)" }}>
          {lockedNote}
        </p>
      ) : null}
    </fieldset>
  );
}
